import React, { useState } from 'react'
import { ReactComponent as TrashIcon } from 'presentation/assets/icons/trash-icon.svg'
import CardNew from 'presentation/shared/components/CardNew'
import DividerNew from 'presentation/shared/components/DividerNew'
import TextFieldNew from 'presentation/shared/components/TextFieldNew'
import HeadingNew from 'presentation/shared/components/HeadingNew'
import SupportTextNew from 'presentation/shared/components/SupportTextNew'
import { ProcedureV2Props } from 'presentation/contexts/surgical-order-v2/types'
import { DataSource } from 'common/enum/data-source'
import * as S from './styles'
import {
  Section,
  Item,
  QuantityAndDeleteRow,
  QuantityFieldWrapper
} from '../components/styles'
import {
  DeleteIconButton,
  StyledCheckbox,
  MainBadge
} from '../../components/styles'
import { AddItemButton, EmptyListState, SectionHeader } from '../components'
import capitalizeFirstLetter from 'common/utils/capitalizeFirstLetter'

type Props = {
  initialRows: ProcedureV2Props[]
  onDelete: (procedureCode: string) => void
  onQuantityChange?: (procedureCode: string, quantity: number) => void
  onToggleSelection?: (procedureCode: string) => void
  hideSections?: boolean
  onAddProcedure?: () => void
  showAddButton?: boolean
  formatDescription?: boolean
}

function ProceduresList({
  initialRows,
  onDelete,
  onQuantityChange,
  onToggleSelection,
  hideSections = false,
  onAddProcedure,
  showAddButton = false,
  formatDescription = false
}: Props) {
  const hospitalRows = initialRows.filter(
    (row) => row.source === DataSource.TEMPLATE
  )
  const doctorRows = initialRows.filter(
    (row) => row.source === DataSource.DOCTOR
  )

  const isFromHospitalTemplate = (procedureCode: string) => {
    return hospitalRows.some((row) => row.code === procedureCode)
  }

  const sections = hideSections
    ? [{ title: '', rows: initialRows }]
    : [
        {
          title: 'Gabarito',
          rows: hospitalRows
        },
        {
          title: 'Inclusões do médico',
          rows: doctorRows
        }
      ]
  const [editingValues, setEditingValues] = useState<{ [key: string]: string }>(
    {}
  )

  const handleQuantityChange = (code: string, value: string) => {
    const numericValue = value.replace(/\D/g, '')
    setEditingValues((prev) => ({ ...prev, [code]: numericValue }))

    if (numericValue !== '') {
      const quantity = parseInt(numericValue, 10)
      if (!isNaN(quantity) && quantity > 0 && quantity <= 99) {
        onQuantityChange?.(code, quantity)
      }
    }
  }

  const handleQuantityBlur = (code: string, value: string) => {
    const numericValue = value.replace(/\D/g, '')
    const quantity = parseInt(numericValue, 10)

    if (isNaN(quantity) || quantity <= 0 || numericValue === '') {
      onQuantityChange?.(code, 1)
    }

    setEditingValues((prev) => {
      const newValues = { ...prev }
      delete newValues[code]
      return newValues
    })
  }

  const handleQuantityFocus = (code: string, currentQuantity: number) => {
    const formattedValue =
      currentQuantity < 10 ? `0${currentQuantity}` : currentQuantity.toString()
    setEditingValues((prev) => ({ ...prev, [code]: formattedValue }))
  }

  const getDisplayQuantity = (code: string, quantity: number): string => {
    const isEditing = code in editingValues
    const displayValue = isEditing
      ? editingValues[code]
      : quantity < 10
      ? `0${quantity}`
      : quantity.toString()
    return displayValue
  }

  if (initialRows.length === 0) {
    return (
      <EmptyListState
        message="Nenhum procedimento foi adicionado ainda"
        showAddButton={showAddButton}
        onAdd={onAddProcedure}
        addLabel="Adicionar outro procedimento"
      />
    )
  }

  return (
    <CardNew
      background="neutral25"
      padding="1rem"
      style={{ borderRadius: '8px' }}
    >
      {sections.map((section, sectionIndex) => {
        if (section.rows.length === 0) return null

        return (
          <Section key={`section-${sectionIndex}`}>
            {!hideSections && section.title && (
              <SectionHeader title={section.title} />
            )}
            {section.rows.map((row, rowIndex) => {
              const displayQuantity = getDisplayQuantity(row.code, row.quantity)
              const isLastRow = rowIndex === section.rows.length - 1

              return (
                <div key={row.code}>
                  <Item>
                    {row.is_main ? (
                      <>
                        <S.ProcedureHeader>
                          <S.CodeAndBadge>
                            <SupportTextNew
                              fontFamily="secondary"
                              size="xxsmall"
                              color="textSecondary"
                              weight="normal"
                              as="span"
                            >
                              Cód. {row.code}
                            </SupportTextNew>
                            <MainBadge>Principal</MainBadge>
                          </S.CodeAndBadge>
                        </S.ProcedureHeader>

                        <HeadingNew
                          size="xxsmall"
                          color="textPrimary"
                          weight="bold"
                          as="div"
                          style={{ marginBottom: '0.5rem' }}
                        >
                          {formatDescription
                            ? capitalizeFirstLetter(row.description)
                            : row.description}
                        </HeadingNew>
                      </>
                    ) : (
                      <S.SecondaryProcedureRow>
                        <StyledCheckbox
                          checked={row.is_selected || false}
                          onChange={() => onToggleSelection?.(row.code)}
                          disabled={!isFromHospitalTemplate(row.code)}
                        />
                        <SupportTextNew
                          size="xxxsmall"
                          weight="semiBold"
                          color="textPrimary"
                          as="span"
                        >
                          Cód. {row.code} - {row.description}
                        </SupportTextNew>
                      </S.SecondaryProcedureRow>
                    )}

                    <QuantityAndDeleteRow>
                      <QuantityFieldWrapper>
                        <TextFieldNew
                          labelOverlapping
                          labelSize="xxsmall"
                          labelWeight="semiBold"
                          label="Quantidade"
                          name={`quantity-${row.code}`}
                          value={displayQuantity}
                          onChange={(e) =>
                            handleQuantityChange(row.code, e.target.value)
                          }
                          onBlur={(e) =>
                            handleQuantityBlur(row.code, e.target.value)
                          }
                          onFocus={() =>
                            handleQuantityFocus(row.code, row.quantity)
                          }
                          maxLength={2}
                          height="40px"
                        />
                      </QuantityFieldWrapper>
                      <DeleteIconButton
                        size="small"
                        onClick={() => onDelete(row.code)}
                        disabled={
                          row.is_main || isFromHospitalTemplate(row.code)
                        }
                      >
                        <TrashIcon width={18} height={18} />
                      </DeleteIconButton>
                    </QuantityAndDeleteRow>
                  </Item>
                  {!isLastRow && (
                    <DividerNew marginTop="0.75rem" marginBottom="0.75rem" />
                  )}
                </div>
              )
            })}
          </Section>
        )
      })}

      {showAddButton && (
        <AddItemButton
          onClick={onAddProcedure}
          label="Adicionar outro procedimento"
        />
      )}
    </CardNew>
  )
}

export default ProceduresList
